import { useState, useMemo, useCallback } from 'react'

import { hasWebViewHeader, normalizeLanguage } from 'utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LanguagesEnum } from 'common/enums/languages'
import { useAppInfo } from 'hooks/useAppInfo'
import { useUpdateAlias } from 'services/account-info/useAccountInfo'
import { UpdateAliasErrorResponseProps } from 'services/account-info/types'
import { Header } from 'components/Header'
import { FeedbackPage } from 'components/FeedbackPage'
import { FeedbackPageFlow, FeedbackPageType } from 'components/FeedbackPage/types'

import { Text, InputText, Button } from '@interco/inter-toranja'
import { FlexColumn, FlexRow } from '@interco/inter-ui/components/Flex'
import {
  TextSize,
  TextType,
  TextWeight,
} from '@interco/inter-toranja/dist/components/Atoms/Text/types'
import { interWbSession } from '@interco/inter-webview-bridge'
import { Spacing } from '@interco/inter-ui/components/Spacing'

import * as S from './styles'
import { data } from './data'
import ValidationItem from './ValidationItem'
import { validateAlias, isAliasValid, AliasValidations } from './validation'
import { ErrorBottomSheet } from './ErrorBottomSheet'
import { AliasErrorCode } from './ErrorBottomSheet/data'

export const EditAlias = () => {
  const [searchParams] = useSearchParams()

  const language = searchParams.get('language')
  const currentAlias = searchParams.get('alias')
  const account = searchParams.get('account') ?? ''
  const cbuCvu = searchParams.get('cbuCvu') ?? ''

  const navigate = useNavigate()

  const normalizedLanguage = normalizeLanguage(language, LanguagesEnum.ES_ES)

  const { appVersion, isDarkMode } = useAppInfo()

  const handleBackButton = () => navigate(-1)
  const hasHeader = hasWebViewHeader(appVersion)

  const [alias, setAlias] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasInteracted, setHasInteracted] = useState<boolean>(false)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [isErrorBottomSheetOpen, setIsErrorBottomSheetOpen] = useState<boolean>(false)
  const [feedbackType, setFeedbackType] = useState<FeedbackPageType | null>(null)

  const { mutate: updateAlias } = useUpdateAlias()

  const labels = data[normalizedLanguage] ?? data['es-ES']

  const validations: AliasValidations = useMemo(() => validateAlias(alias), [alias])
  const showValidationFeedback = hasInteracted && alias.length > 0

  const isFormValid = useMemo(() => isAliasValid(alias), [alias])

  const handleAliasChange = (value: string) => {
    setAlias(value)
    if (!hasInteracted) {
      setHasInteracted(true)
    }
  }

  const handleEditAlias = useCallback(() => {
    setIsLoading(true)

    updateAlias(
      {
        cbuCvu,
        alias,
      },
      {
        onSuccess: () => {
          setIsLoading(false)
          setFeedbackType(FeedbackPageType.SUCCESS)

          interWbSession.requestAnalytics('DADOS_BANCARIOS_GA_FLUXO', {
            flow: 'dados-bancarios',
            screen: 'edit-alias',
            ref_type: 'button',
            content_action: 'touch',
            action_id: 'save_alias_success',
          })
        },

        onError: (error: UpdateAliasErrorResponseProps) => {
          setIsLoading(false)
          setErrorCode(error.codigo)
          setIsErrorBottomSheetOpen(true)
        },
      },
    )
  }, [alias, cbuCvu, updateAlias])

  if (feedbackType) {
    return (
      <FeedbackPage
        feedbackType={feedbackType}
        flow={FeedbackPageFlow.EDIT_ALIAS}
        account={account}
      />
    )
  }

  return (
    <>
      <FlexColumn height="100vh" justifyContent="space-between">
        {hasHeader ? (
          <Header onClick={handleBackButton} isDarkMode={isDarkMode} />
        ) : (
          <Spacing mb="sm" />
        )}

        <FlexColumn className="px-6 py-0" height="100%" justifyContent="start" gap="24px">
          <Text
            textType={TextType.Title}
            textSize={TextSize.Large}
            as="h1"
            textWeight={TextWeight.Bold}
          >
            {labels.editAliasTitle}
          </Text>

          <FlexColumn gap="12px">
            <FlexColumn gap="12px">
              <Text
                textSize={TextSize.Large}
                textWeight={TextWeight.Regular}
                textType={TextType.Label}
              >
                {labels.editAliasSubtitle}
              </Text>

              {/* Current alias display */}
              <FlexRow
                alignItems="center"
                justifyContent="space-between"
                style={{ padding: '8px 0' }}
              >
                <Text
                  textSize={TextSize.Medium}
                  textWeight={TextWeight.Regular}
                  textType={TextType.Label}
                >
                  {labels.currentAliasLabel}
                </Text>
                <Text
                  textSize={TextSize.Medium}
                  textWeight={TextWeight.Bold}
                  textType={TextType.Body}
                >
                  {currentAlias}
                </Text>
              </FlexRow>
            </FlexColumn>

            {/* New alias input */}
            <FlexColumn gap="24px">
              <S.InputContainer>
                <InputText
                  showClear
                  type="text"
                  value={alias}
                  state="enabled"
                  id="alias-input"
                  label={labels.newAliasLabel}
                  onChange={(value: string) => handleAliasChange(value)}
                />
              </S.InputContainer>

              {/* Validation rules */}
              <FlexColumn gap="8px">
                <Text
                  textSize={TextSize.Medium}
                  textWeight={TextWeight.Bold}
                  textType={TextType.Body}
                >
                  {labels.rulesTitle}
                </Text>

                <FlexColumn gap="4px">
                  <ValidationItem
                    isValid={validations.lengthValid}
                    text={labels.rule1}
                    showFeedback={showValidationFeedback}
                  />
                  <ValidationItem
                    isValid={validations.charsValid}
                    text={labels.rule2}
                    showFeedback={showValidationFeedback}
                  />
                  <ValidationItem
                    isValid={validations.noInvalidChars}
                    text={labels.rule3}
                    showFeedback={showValidationFeedback}
                  />
                </FlexColumn>
              </FlexColumn>
            </FlexColumn>
          </FlexColumn>
        </FlexColumn>

        {/* Save button */}
        <FlexColumn className="px-6 pb-6">
          <Button
            hierarchy="primary"
            label={labels.saveButton}
            onClick={handleEditAlias}
            disabled={!isFormValid || isLoading}
            loading={isLoading}
            style={{ width: '100%' }}
          />
        </FlexColumn>
      </FlexColumn>

      {isErrorBottomSheetOpen && (
        <ErrorBottomSheet
          errorCode={(errorCode as AliasErrorCode) ?? AliasErrorCode.ERROR_ON_UPDATING_ALIAS}
          normalizedLanguage={normalizedLanguage}
          handleCloseBottomSheet={() => {
            setIsErrorBottomSheetOpen(false)
          }}
        />
      )}
    </>
  )
}
